import React, { useState, useEffect } from 'react';
import {
  BarChart2, AlertCircle, DollarSign, Users,
  ChevronRight, ChevronLeft,
} from 'lucide-react';
import './RyanAdminSidebar.css';

export type RyanAdminPage = 'stats' | 'errors' | 'sales' | 'accounts';

interface RyanAdminSidebarProps {
  activePage: RyanAdminPage;
  onPageChange: (page: RyanAdminPage) => void;
  errorCount?: number;
}

interface SidebarItem {
  id: RyanAdminPage;
  icon: React.ComponentType<any>;
  label: string;
  sublabel: string;
}

const ITEMS: SidebarItem[] = [
  { id: 'stats',    icon: BarChart2,   label: 'Estatísticas',    sublabel: 'Usuários e engajamento'     },
  { id: 'errors',   icon: AlertCircle, label: 'Erros',           sublabel: 'Flashcards reportados'      },
  // Produtos, marketing, campanhas, embaixadores, vendas, acesso e
  // rentabilidade viraram uma página só com barra de abas própria
  // (RyanAdminHub) — antes eram duas entradas separadas aqui (products/sales).
  { id: 'sales',    icon: DollarSign,  label: 'Gestão',          sublabel: 'Produtos, vendas e acesso'  },
  { id: 'accounts', icon: Users,       label: 'Contas',          sublabel: 'VIP, editores e permissões' },
];

// Groups separated by dividers
const GROUPS: RyanAdminPage[][] = [
  ['stats', 'errors'],
  ['sales'],
  ['accounts'],
];

const STORAGE_KEY = 'ryan_admin_sidebar_collapsed';
const MOBILE_QUERY = '(max-width: 768px)';

const RyanAdminSidebar: React.FC<RyanAdminSidebarProps> = ({
  activePage, onPageChange, errorCount = 0,
}) => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) !== 'false';
    } catch {
      return true;
    }
  });

  // No mobile a sidebar expandida (220px) não cabe — força sempre o modo
  // compacto (só ícones, 56px) independente da preferência salva no desktop,
  // e esconde o botão de expandir/recolher (não faz sentido nesse contexto).
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const effectiveCollapsed = isMobile || collapsed;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(collapsed));
    if (effectiveCollapsed) {
      document.body.classList.add('ryan-sidebar-collapsed');
    } else {
      document.body.classList.remove('ryan-sidebar-collapsed');
    }
    return () => {
      document.body.classList.remove('ryan-sidebar-collapsed');
    };
  }, [collapsed, effectiveCollapsed]);

  const toggle = () => setCollapsed(v => !v);

  const renderNav = () => {
    const nodes: React.ReactNode[] = [];
    GROUPS.forEach((group, gi) => {
      group.forEach(id => {
        const item = ITEMS.find(it => it.id === id)!;
        const Icon = item.icon;
        const isActive = activePage === item.id;
        const badgeCount = item.id === 'errors' ? errorCount : 0;
        const hasBadge = badgeCount > 0;
        nodes.push(
          <button
            key={item.id}
            className={`ryan-admin-sidebar-item${isActive ? ' active' : ''}`}
            onClick={() => onPageChange(item.id)}
            title={effectiveCollapsed ? `${item.label}\n${item.sublabel}` : undefined}
          >
            <span className="ryan-admin-sidebar-item-icon">
              <Icon size={effectiveCollapsed ? 21 : 20} strokeWidth={1.5} />
            </span>
            {!effectiveCollapsed && (
              <span className="ryan-admin-sidebar-item-text">
                <span className="ryan-admin-sidebar-item-label">{item.label}</span>
                <span className="ryan-admin-sidebar-item-sublabel">{item.sublabel}</span>
              </span>
            )}
            {hasBadge && (
              <span className="ryan-admin-sidebar-badge">{badgeCount}</span>
            )}
          </button>
        );
      });
      if (gi < GROUPS.length - 1) {
        nodes.push(<div key={`div-${gi}`} className="ryan-admin-sidebar-divider" />);
      }
    });
    return nodes;
  };

  return (
    <>
      {!isMobile && (
        <button
          className={`ryan-admin-sidebar-toggle${collapsed ? ' collapsed' : ''}`}
          onClick={toggle}
          aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
          title={collapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      )}

      <aside className={`ryan-admin-sidebar${effectiveCollapsed ? ' collapsed' : ''}`}>
        <nav className="ryan-admin-sidebar-nav">
          {renderNav()}
        </nav>
      </aside>
    </>
  );
};

export default RyanAdminSidebar;
